<template>
  <div v-if="!isLoading">
    <ViewHeader :title="editId === 'create' ? 'Create member' : 'Edit member'">
      <CButton
        color="primary"
        class="ra-button"
        @click="saveMember"
        :disabled="allow_save_user"
      >
        <Icon name="plus" class="mr-1" v-if="editId === 'create'"/>
        <Icon name="save" class="mr-1" v-else/>
        {{ editId ? "Save" : "Create" }}
      </CButton>
    </ViewHeader>

    <div v-if="financialManagementScopes && financialManagementScopes.length">
      <CRow>
        <CCol>
          <InputComponent
            v-for="formField in Object.values(memberFields)"
            class="mb-3"
            :key="formField.name"
            :formField="formField"
            :incorrectInputValues="incorrectInputValues.includes(formField.name)"
          />
        </CCol>
        <CCol>
          <InputComponent
            v-for="formField in Object.values(notificationColumn)"
            class="mb-3 w-50 mr-auto ml-auto"
            :key="formField.name"
            :formField="formField"
            :incorrectInputValues="incorrectInputValues.includes(formField.name)"
            @select="({ value }) => notificationColumn[formField.name].value = value"
          />
        </CCol>
      </CRow>
      <div>
        <label
          :class="
            incorrectInputValues.includes('roles')
              ? 'text-danger'
              : 'text-muted'
          "
          class="font-weight-bold text-capitalize"
        >
          roles *
        </label>
        <div class="d-flex align-items-center justify-content-between gap-5">
          <div
            class="text-primary border border-primary text-muted px-3 py-2 custom-checkbox-style"
            v-for="scope in scopes"
          >
            <div class="d-flew align-items-center">
              <CInputRadio
                :label="scope.name"
                :checked="selectedScope.includes(scope.name)"
                @change="() => handleRoleChange(scope.name)"
                custom
                inline
              />
            </div>
            <p class="pl-4 text-dark">{{ scope.description }}</p>
          </div>
        </div>
      </div>
      <div v-if="selectedScope.includes('Restricted scope')" class="mt-2">
        <label
          :class="
            incorrectInputValues.includes('financial_scope')
              ? 'text-danger'
              : 'text-muted'
          "
          class="font-weight-bold text-capitalize"
        >
          Please select Financial management scope *
        </label>
        <div class="d-flex align-items-center gap-5">
          <CInputCheckbox
            v-for="scope in financialManagementScopes"
            :key="scope"
            :label="scope"
            :custom="true"
            :checked="selectedFinancialScopes.includes(scope)"
            class="input-to-left"
            add-label-classes="font-weight-bold"
            @update:checked="handleFinancialScopeSelect(scope)"
          />
        </div>
      </div>
    </div>
  </div>
  <DotsLoader v-else />
</template>

<script>
import InputComponent from "@/components/InputComponent";
import { UsersService } from "@/services/users";

const scopes = [
  {
    name: "Admin",
    description:
      "Admin can create, assign and edit all\n" +
      "ticket on any entity and can manage\n" +
      "members (create, delete and\n" +
      "permission) They can manage Label and\n" +
      "Entity list per financial direction.",
  },
  {
    name: "Full scope",
    description:
      "Full scope are able to create, assign and edit\n" +
      "all ticket on any entity. They can also create\n" +
      "new label and view entity list per financial\n" +
      "direction",
  },
  {
    name: "Restricted scope",
    description:
      "Restricted scope can only work each ticket\n" +
      "on each financial management in their\n" +
      "scope. They can also create a new label",
  },
];

export default {
  name: "MembersCreate",
  components: {
    InputComponent,
  },
  data() {
    return {
      scopes,
      editId: null,
      isLoading: false,
      financialManagementScopes: [],
      selectedFinancialScopes: [],
      selectedScope: [],
      incorrectInputValues: [],
      changedProperties: {},
      memberFields: {
        email: {
          name: "email",
          label: "Email Adress",
          placeholder: "Please complet mail address",
          value: "",
          type: "text",
          required: true,
          fullWidth: true,
          validation: /\S+@\S+\.\S+/,
        },
        firstname: {
          name: "firstname",
          label: "First name",
          placeholder: "Please complet first name",
          value: "",
          type: "text",
          required: true,
          fullWidth: true,
        },
        lastname: {
          name: "lastname",
          label: "Last Name",
          placeholder: "Please complet last name",
          value: "",
          type: "text",
          required: true,
          fullWidth: true,
        },
        password: {
          name: "password",
          label: "Password",
          guidanceText: "Password must be at least 6 characters",
          guidanceCondition: (value) => !value || value.length < 6,
          value: "",
          type: "password",
          fullWidth: true,
          autocomplete: "new-password",
        },
      },
      notificationColumn: {
        email_notification: {
          name: "email_notification",
          label: "Email Notification",
          value: true,
          type: "select",
          required: false,
          fullWidth: false,
          options: [
            {value: false, label: "No"},
            {value: true, label: "Yes"}
          ]
        }
      }
    };
  },
  computed: {
    postBody() {
      return {
        email: this.memberFields.email.value,
        firstname: this.memberFields.firstname.value,
        lastname: this.memberFields.lastname.value,
        password: this.memberFields.password.value,
        roles: this.selectedScope,
        email_notification: this.notificationColumn.email_notification.value,
        financial_role: this.selectedScope.includes('Restricted scope') ? this.selectedFinancialScopes : [],
      };
    },
    allow_save_user() {
      return !(this.postBody?.roles?.length && this.postBody.email !== '' && this.postBody.firstname !== '' && this.postBody.lastname !== '')
    },
  },
  async mounted() {
    this.isLoading = true;
    this.editId = this.$route.params.id || "create";
    if (this.editId !== "create") {
      await this.populateFields();
    }
    const roles = await UsersService.getRoles();
    this.financialManagementScopes = roles.financial_role;
    this.isLoading = false;
  },
  methods: {
    async populateFields() {
      const member = await UsersService.getUser(this.editId);
      this.selectedScope = member.roles;
      Object.entries(member).forEach(([property, propertyValue]) => {
        if (property === "roles") this.selectedScope = propertyValue;
        if (property === "financial_role")
          this.selectedFinancialScopes = propertyValue || [];
        else if (this.memberFields[property])
          this.memberFields[property].value = propertyValue;
      });
    },
    handleRoleChange(option) {
      this.selectedScope = [option];
    },
    handleFinancialScopeSelect(option) {
      if (this.selectedFinancialScopes.includes(option))
        return (this.selectedFinancialScopes = [
          ...this.selectedFinancialScopes,
        ].filter((scope) => scope !== option));
      this.selectedFinancialScopes.push(option);
    },
    memberDataCorrect() {
      this.incorrectInputValues = [];

      Object.values(this.memberFields).forEach((formField) => {
        if (!formField.value && formField.required)
          this.incorrectInputValues.push(formField.name);
        if (
          formField.type === "password" &&
          formField.value &&
          formField.value.length < 6
        )
          this.incorrectInputValues.push(formField.name);
        else if (formField.validation) {
          const re = new RegExp(formField.validation);
          if (!re.test(formField.value))
            this.incorrectInputValues.push(formField.name);
        }
      });

      if (!this.selectedScope) this.incorrectInputValues.push("roles");
      if (
        this.selectedScope.includes("Restricted scope") &&
        !this.selectedFinancialScopes.length
      )
        this.incorrectInputValues.push("financial_scope");
      return this.incorrectInputValues.length === 0;
    },
    async saveMember() {
      if (!this.memberDataCorrect())
        return alert("Please fill in all required fields");

      Object.keys(this.postBody).forEach((property) => {
          if (this.postBody[property]) this.changedProperties[property] = this.postBody[property];
      });

      const {res, data} = await UsersService.editOrCreateUser(
        this.editId,
        this.changedProperties
      );
      if ([200, 201].includes(res.status))
        await this.$router.push({
          name: "Members",
        });
      else {
        alert(data.detail === 'REGISTER_USER_ALREADY_EXISTS' ? `User with email ${this.memberFields.email.value} already exists.` : data.detail);
      }
    },
  },
};
</script>

<style scoped>
.custom-checkbox-style p {
  font-size: 12px !important;
}
</style>
